/**
 * NotificationBell Component
 * Bell icon with unread count badge
 *
 * Phase 2 Features:
 * - Smart polling: Optionally polls every 30 seconds when tab is active
 * - Refetch on navigation: Automatically refetches when navigating
 * - Cost-effective: Polling only when tab active, disabled in background
 */

import React, { useState } from 'react';
import { Bell } from 'lucide-react';
import { useUnreadCount } from '../../hooks/useNotifications';
import { useRefetchOnNavigation } from '../../hooks/useRefetchOnNavigation';
import { NotificationCenter } from './NotificationCenter';

export interface NotificationBellProps {
  /**
   * Enable smart polling (only when tab is active)
   * Set to number of milliseconds (e.g., 30000 for 30 seconds)
   * Default: false (disabled)
   */
  enablePolling?: number | false;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  enablePolling = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Phase 2: Smart polling (only when tab active)
  const { data: unreadData, refetch } = useUnreadCount({
    refetchInterval: enablePolling,
  });

  // Phase 2: Refetch on navigation
  useRefetchOnNavigation(refetch);

  const unreadCount = unreadData?.count || 0;

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative rounded-md p-2 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        <Bell className="h-6 w-6" />

        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Center Dropdown */}
      {isOpen && (
        <NotificationCenter
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};
